import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BasicButton from '../Common/Button/BasicButton';
import AlertModal from '@/components/Common/Modal/AlertModal';
import PhoneAuth from '@/components/Common/PhoneAuth';
import MenuHeader from '@/components/Header/MenuHeader';
import { CheckCircleOutlineIcon, CheckCircleSolidIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { DEALER_BLACK_LIST_MEMBER_MESSAGE } from '@/const/errorMessage';
import { Checkbox, Divider } from '@mui/material';

interface SignUpProps {
  memberInfo: MemberInfo;
  updateMemberInfo: (data: MemberInfo) => void;
  isFirst: boolean;
  setIsFirst: React.Dispatch<React.SetStateAction<boolean>>;
  isDealerSignUp?: boolean;
}

const PhoneAuthAndTerms = ({
  memberInfo,
  updateMemberInfo,
  isFirst: _isFirst,
  setIsFirst,
  isDealerSignUp = false,
}: SignUpProps) => {
  const navigate = useNavigate();
  const [checkList, setCheckList] = useState<string[]>([]);
  const [isShowDealerBlackListPopup, setIsShowDealerBlackListPopup] = useState(false);

  const agreementList = [
    { id: 'terms', title: '이용 약관', isRequired: true, link: '#', type: 'USE_TERMS' },
    {
      id: 'personalInfo',
      title: '개인정보 수집 및 이용 동의',
      isRequired: true,
      link: '#',
      type: 'PERSONAL_INFO_TERMS',
    },
    { id: 'marketing', title: '마케팅 정보 수신 및 활용 동의', isRequired: true, link: '#', type: 'MARKETING' },
  ];

  useEffect(() => {
    const conditions: { [key: string]: string } = {
      terms: 'terms',
      personalInfo: 'personalInfo',
      marketing: 'marketing',
    };

    const newCheckList = Object.keys(conditions)
      .filter((key) => memberInfo[key])
      .map((key) => conditions[key]);

    setCheckList(newCheckList);
  }, [memberInfo]);

  const isDealerChecked = Boolean(memberInfo.dealerTerms);

  const isSuccess = useMemo(() => {
    const requiredIds = agreementList.filter((el) => el.isRequired).map((el) => el.id);
    const allRequiredAgreed = requiredIds.every((id) => checkList.includes(id));
    const dealerAgreed = !isDealerSignUp || isDealerChecked;
    return memberInfo.isAuthenticateCode && allRequiredAgreed && dealerAgreed;
  }, [memberInfo.isAuthenticateCode, checkList, isDealerChecked, isDealerSignUp]);

  const goNext = () => {
    updateMemberInfo({
      ...memberInfo,
      marketing: checkList.includes('marketing'),
      terms: checkList.includes('terms'),
      personalInfo: checkList.includes('personalInfo'),
    });
    setIsFirst(false);
  };

  const handleSingleCheck = (checked: boolean, id: string) => {
    updateMemberInfo({
      ...memberInfo,
      [id]: checked,
    });
  };

  const handleCheckAll = (checked: boolean) => {
    updateMemberInfo({
      ...memberInfo,
      marketing: checked,
      terms: checked,
      personalInfo: checked,
    });
  };

  const onClickTermDetail = (type: string) => {
    navigate(`/terms${isDealerSignUp ? '/dealer' : ''}/${type}`);
  };

  const goToDealerSignUp = () => {
    setIsShowDealerBlackListPopup(false);
    navigate('/signup', { replace: true, state: { signUpType: 'DEALER' } });
  };

  return (
    <>
      <MenuHeader title="전화번호로 회원가입"></MenuHeader>
      <div className="flex flex-col justify-between w-full pt-4 px-4 h-[100dvh]">
        <div>
          <p className="text-[22px] font-bold mt-6 mb-[30px] pt-6 text-gray-8">휴대폰 번호를 인증해주세요</p>
          <PhoneAuth
            isSign={true}
            memberInfo={memberInfo}
            updateMemberInfo={(data) => updateMemberInfo(data)}
            signUpType={isDealerSignUp ? 'DEALER' : 'NORMAL'}
            onDealerBlackList={() => setIsShowDealerBlackListPopup(true)}
          ></PhoneAuth>

          <section className="mt-[70px] pb-8">
            <div className="text-sm pb-[3px]">
              <Checkbox
                icon={<CheckCircleOutlineIcon color={colors.gray[4]} />}
                checkedIcon={<CheckCircleSolidIcon color={colors.primary[10]} />}
                sx={{
                  borderWidth: '4px',
                  color: colors.gray[4],
                  '&.Mui-checked': {
                    color: colors.primary[10],
                  },
                }}
                onChange={(e) => handleCheckAll(e.target.checked)}
                checked={checkList.length === agreementList.length}
              />
              <span
                className="text-gray-8 font-medium"
                onClick={() => handleCheckAll(checkList.length !== agreementList.length)}
              >
                서비스 이용 약관 전체 동의하기
              </span>
            </div>

            <Divider></Divider>

            {agreementList?.map((item, index) => (
              <div key={index}>
                <div
                  className={`${isDealerSignUp ? 'pl-4' : ''} flex justify-between text-sm ${
                    index === 0 ? 'pt-[3px]' : ''
                  }`}
                >
                  <div className="flex items-top">
                    <Checkbox
                      icon={<CheckCircleOutlineIcon color={colors.gray[4]} />}
                      checkedIcon={<CheckCircleSolidIcon color={colors.primary[10]} />}
                      sx={{
                        color: colors.gray[4],
                        '&.Mui-checked': {
                          color: colors.primary[10],
                        },
                      }}
                      onChange={(e) => handleSingleCheck(e.target.checked, item.id)}
                      checked={checkList.includes(item.id)}
                    />
                    <div
                      className="text-gray-8 font-medium py-2 pr-2"
                      onClick={() => handleSingleCheck(!checkList.includes(item.id), item.id)}
                    >
                      {item.title}
                      <span className="text-gray-6 font-normal">{item.isRequired ? '(필수)' : '(선택)'}</span>
                    </div>
                  </div>

                  <span
                    className="text-gray-6 font-semibold text-xs leading-10 cursor-pointer w-[30px]"
                    onClick={() => onClickTermDetail(item.type)}
                  >
                    보기
                  </span>
                </div>
              </div>
            ))}

            {isDealerSignUp && (
              <>
                <Divider></Divider>
                <div className="flex justify-between text-sm">
                  <div className="text-sm pb-[3px]">
                    <Checkbox
                      icon={<CheckCircleOutlineIcon color={colors.gray[4]} />}
                      checkedIcon={<CheckCircleSolidIcon color={colors.primary[10]} />}
                      sx={{
                        borderWidth: '4px',
                        color: colors.gray[4],
                        '&.Mui-checked': {
                          color: colors.primary[10],
                        },
                      }}
                      onChange={(e) => handleSingleCheck(e.target.checked, 'dealerTerms')}
                      checked={isDealerChecked}
                    />
                    <span
                      className="text-gray-8 font-medium"
                      onClick={() => handleSingleCheck(!isDealerChecked, 'dealerTerms')}
                    >
                      딜러 약정서 동의
                      <span className="text-gray-6 font-normal">(필수)</span>
                    </span>
                  </div>
                  <span
                    className="text-gray-6 font-semibold text-xs leading-10 cursor-pointer w-[30px]"
                    onClick={() => onClickTermDetail('DEALER_AGREEMENT')}
                  >
                    보기
                  </span>
                </div>

                <ol className="list-decimal pl-12 flex flex-col gap-3 text-sm">
                  <li>사원증 제출 및 자격 조회에 동의합니다.</li>
                  <li>딜러회원 매물은 검수 후 노출 원칙에 동의합니다.</li>
                  <li>문의 응대는 직트럭(영업팀/CS) 우선 접수 후 연결 방식에 동의합니다.</li>
                  <li>허위/미끼/과장 매물 등록 금지 및 정보 진실성에 서약합니다.</li>
                  <li>문의자 정보는 필요 최소 정보만 제공되며, 목적 외 사용/제3자 제공 금지에 동의합니다.</li>
                  <li>위반 시 노출 제한/정지/해지 및 손해배상 조치에 동의합니다.</li>
                </ol>
              </>
            )}
          </section>
        </div>
        <div className="pb-2">
          <BasicButton
            name="다음"
            bgColor={colors.primary[10]}
            borderColor={colors.primary[10]}
            textColor={colors.gray[0]}
            fontSize={16}
            height={48}
            borderRadius={10}
            fontWeight="semibold"
            onClick={() => goNext()}
            isDisabled={!isSuccess}
          ></BasicButton>
        </div>
      </div>

      <AlertModal
        isOpen={isShowDealerBlackListPopup}
        title="딜러 회원 안내"
        content={<div className="break-keep whitespace-pre-line">{DEALER_BLACK_LIST_MEMBER_MESSAGE}</div>}
        buttonLabel="딜러 회원가입 하기"
        onClose={goToDealerSignUp}
      />
    </>
  );
};

export default PhoneAuthAndTerms;
